import React from 'react';
import { observer } from 'mobx-react';
import { Avatar, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, Tooltip, useTheme } from '@mui/material';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import { useTranslation } from 'react-i18next';
import { FormLineName } from './const';
import useData from './hooks';
import DeclarationAlreadySavedDialog from '10.quickConnect.app/components/shared/Dialogs/DeclarationAlreadySavedDialog';
import OpenedElsewherePrompt from '10.quickConnect.app/components/shared/Dialogs/OpenedElsewherePrompt';

import { getFormIcon } from '10.quickConnect.app/components/domain/Home/helpers';
import {
  CustomListItemText,
  getAvatarStyle as getAvatarStyle,
  getDuplicateIconStyle,
  getListItemAvatarStyle,
  getListItemButtonStyle,
} from '10.quickConnect.app/components/domain/Home/styles';
import { TreeDataItem } from '30.quickConnect.Stores/helpers/type';
import { FormViewer } from '90.quickConnect.Models/models';

const FormLine = (form: TreeDataItem) => {
  const { id, formType, name, description } = form as FormViewer;
  const { t } = useTranslation('forms');

  const {
    choiceDialogOpen,
    drafts,
    onClickForm,
    createDeclaration,
    closeChoiceDialog,
    openedElsewherePromptProps,
    isMobile,
    isHover,
    setIsHover,
    toggleFavorite,
    isFavorite,
    formsAsTree,
    isFavoritesFeatureEnabled,
  } = useData(form as FormViewer);

  const theme = useTheme();

  return (
    <ListItem data-cy={FormLineName} disablePadding>
      <ListItemButton
        sx={getListItemButtonStyle(theme)}
        data-cy={`${FormLineName}-${id}`}
        onContextMenu={(event) => event.preventDefault()}
        onMouseOver={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={onClickForm}
      >
        <ListItemAvatar sx={getListItemAvatarStyle(theme)}>
          <Avatar sx={getAvatarStyle(theme)}>{getFormIcon(formType)}</Avatar>
        </ListItemAvatar>
        <CustomListItemText primary={<span data-cy={`${FormLineName}-name`}>{name}</span>} secondary={description} />
        {isFavoritesFeatureEnabled && (!formsAsTree || isMobile || isHover) && (
          <Tooltip title={isFavorite ? t('qcapp_remove_from_favorites') : t('qcapp_add_to_favorites')}>
            <ListItemIcon sx={getDuplicateIconStyle()} onClick={toggleFavorite} data-cy={`${FormLineName}-favorite`}>
              {isFavorite ? (
                // Etat du favori annoncé par les lecteurs d'écran, sur l'étoile pleine seulement comme sur mobile
                // (la non-régression le lit aussi)
                <StarOutlinedIcon
                  sx={{ color: 'goldenrod' }}
                  aria-label={t('qcapp_favorite_forms')}
                  data-cy={`${FormLineName}-favorite-icon`}
                />
              ) : (
                <StarBorderIcon data-cy={`${FormLineName}-favorite-icon`} />
              )}
            </ListItemIcon>
          </Tooltip>
        )}
      </ListItemButton>
      <DeclarationAlreadySavedDialog
        drafts={drafts}
        onCreateDeclaration={createDeclaration}
        open={choiceDialogOpen}
        onClose={closeChoiceDialog}
      />
      <OpenedElsewherePrompt {...openedElsewherePromptProps} />
    </ListItem>
  );
};

export default observer(FormLine);
